/**
 * Dates and times from the server (ISO 8601, UTC) in the user's language and time zone.
 * A text that is not a date is shown as it came, so nothing is hidden.
 */
export function formatDateTime(isoText: string, language: string | undefined): string {
  return formatIso(isoText, language, { dateStyle: "medium", timeStyle: "short" });
}

/** Weekday and time, for moments within the next days (when a snoozed Thread wakes, F44). */
export function formatWeekdayTime(isoText: string, language: string | undefined): string {
  return formatIso(isoText, language, { weekday: "short", hour: "numeric", minute: "2-digit" });
}

/** The day only, for facts whose time of day does not matter (when a capability was observed). */
export function formatDate(isoText: string, language: string | undefined): string {
  return formatIso(isoText, language, { dateStyle: "medium" });
}

/**
 * A date as a source gives it, in the user's language: a year, a month of a year, or a day. Its numbers stay as written,
 * in no time zone and converted to no other calendar, so a medieval date reads as the record has it.
 */
export function formatCalendarDate(year: number, month: number | null, day: number | null, language: string | undefined): string {
  // The month's name and the language's order come from a leap year that has every day, with the source's own year put
  // in: a date the Gregorian calendar lacks, such as a Julian 29 February 1500, is not moved (Sol R8).
  const probe = new Date(Date.UTC(2000, (month ?? 1) - 1, day ?? 1));
  if (probe.getUTCMonth() !== (month ?? 1) - 1 || probe.getUTCDate() !== (day ?? 1)) {
    return [day, month, year].filter((part) => part !== null).join(".");
  }
  const options: Intl.DateTimeFormatOptions = { timeZone: "UTC", year: "numeric", ...(month === null ? {} : { month: "short" }), ...(month === null || day === null ? {} : { day: "numeric" }) };
  return new Intl.DateTimeFormat(language, options).formatToParts(probe).map((part) => (part.type === "year" ? String(year) : part.value)).join("");
}

function formatIso(isoText: string, language: string | undefined, options: Intl.DateTimeFormatOptions): string {
  const time = Date.parse(isoText);
  if (Number.isNaN(time)) {
    return isoText;
  }
  return new Intl.DateTimeFormat(language, options).format(time);
}
